/**
 * Tone for the value text — colour intent only. Defaults to `default` (the
 * surface foreground), with `success` / `caution` / `error` / `primary` /
 * `info` matching the M3 palette so a "97% success rate" stat can render
 * green and a "3 failures" stat can render red.
 */
export const STAT_TILE_TONE_DEFAULT = 'default';
export const STAT_TILE_TONE_PRIMARY = 'primary';
export const STAT_TILE_TONE_SUCCESS = 'success';
export const STAT_TILE_TONE_CAUTION = 'caution';
export const STAT_TILE_TONE_ERROR = 'error';
export const STAT_TILE_TONE_INFO = 'info';

export type StatTileTone =
  | typeof STAT_TILE_TONE_DEFAULT
  | typeof STAT_TILE_TONE_PRIMARY
  | typeof STAT_TILE_TONE_SUCCESS
  | typeof STAT_TILE_TONE_CAUTION
  | typeof STAT_TILE_TONE_ERROR
  | typeof STAT_TILE_TONE_INFO;

/** Stack alignment. Defaults to `start` — left-aligned matches every consumer surface. */
export const STAT_TILE_ALIGN_START = 'start';
export const STAT_TILE_ALIGN_CENTER = 'center';

export type StatTileAlign =
  | typeof STAT_TILE_ALIGN_START
  | typeof STAT_TILE_ALIGN_CENTER;

export const STAT_TILE_SIZE_SM = 'sm';
export const STAT_TILE_SIZE_MD = 'md';
export const STAT_TILE_SIZE_LG = 'lg';

export type StatTileSize =
  | typeof STAT_TILE_SIZE_SM
  | typeof STAT_TILE_SIZE_MD
  | typeof STAT_TILE_SIZE_LG;

/**
 * Whether the label is automatically uppercased (the classic "STAT" small
 * caps look) or left as-is. Defaults to `uppercase` for backwards compat
 * with existing dashboards that pass mixed-case strings and rely on the
 * component to normalise them. Pass `none` when the design preserves
 * mixed-case labels (e.g. "Total runs").
 */
export const STAT_TILE_LABEL_TRANSFORM_UPPERCASE = 'uppercase';
export const STAT_TILE_LABEL_TRANSFORM_NONE = 'none';

export type StatTileLabelTransform =
  | typeof STAT_TILE_LABEL_TRANSFORM_UPPERCASE
  | typeof STAT_TILE_LABEL_TRANSFORM_NONE;

/**
 * Per-size value font size, label font size, and gap between them. Picked to
 * match three real consumer slots: `sm` for the canvas-stats grid (compact),
 * `md` for the run-history analytics strip (page-level), `lg` for hero KPI
 * tiles on a dashboard.
 */
export const STAT_TILE_SIZE_TOKENS: Record<
  StatTileSize,
  {
    valueFontSize: string;
    valueLineHeight: string;
    valueFontWeight: string;
    valueLetterSpacing: string;
    labelFontSize: string;
    labelLetterSpacing: string;
    gap: string;
  }
> = {
  [STAT_TILE_SIZE_SM]: {
    valueFontSize: '20px',
    valueLineHeight: '24px',
    valueFontWeight: '700',
    valueLetterSpacing: '-0.3px',
    labelFontSize: '10.5px',
    labelLetterSpacing: '0.02em',
    gap: '3px',
  },
  [STAT_TILE_SIZE_MD]: {
    valueFontSize: '24px',
    valueLineHeight: '1.1',
    valueFontWeight: '600',
    valueLetterSpacing: '-0.3px',
    labelFontSize: '12px',
    labelLetterSpacing: '0.04em',
    gap: '4px',
  },
  [STAT_TILE_SIZE_LG]: {
    valueFontSize: '32px',
    valueLineHeight: '1.05',
    valueFontWeight: '600',
    valueLetterSpacing: '-0.3px',
    labelFontSize: '13px',
    labelLetterSpacing: '0.04em',
    gap: '6px',
  },
};
